import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { useEffect, useReducer, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import Webcam from 'react-webcam'
// import { useDebounce } from 'use-debounce'
import { useUpdateKycMutation } from '~/app/store/services'
import { handleStartRecording, handleStopRecording } from '~/utils'
import KycFaceInstructions from './KycFaceInstructions'
import OvalOverlay from './styled/OvalOverlay'
import { t, type Phrase } from '~/utils/narration'
// import { log } from 'console'

type Step = {
  id: number
  // type: 'LIGHT' | 'GLASS' | 'MASK' | 'BLINK' | 'HAND' // Add possible step types
  type: 'LIGHT' | 'HAND' // Add possible step types
  name: Phrase
  instructions: Phrase[]
  entryVoicePrompt?: string
  successMessage: Phrase
}

type State = {
  currentStep: number
  status: 'pending' | 'success' | 'fail'
  completed: boolean
  warningMessage: string
  faceCountWarningMessage: string
  enteredAt: number // <-- Add timestamp for state entry
  successAt: number | null // <-- Add timestamp for success
  receivedSuccess: boolean // <-- Flag to show success message
  successCount: number // <-- Add this to track success signals
}

type Action =
  | { type: 'NEXT_STEP'; totalSteps: number }
  | { type: 'UPDATE_STATUS'; payload: { status: 'success' | 'fail'; warningMessage: string } }
  | { type: 'SET_FACE_COUNT_WARNING'; payload: string }
  | { type: 'SET_ENTERED_AT'; payload: number } // <-- Action for setting entry time
  | { type: 'RECEIVE_SUCCESS'; payload: { requiredSuccessCount: number } }
  | { type: 'SET_SUCCESS_AT' }
  | { type: 'RESET_SUCCESS_COUNT' } // <-- Reset success count for new step

// Fixed steps
const fixedSteps: Step[] = [
  {
    id: 1,
    type: 'LIGHT',
    name: { en: 'Checking lighting conditions', hi: 'रोशनी की स्थिति जाँची जा रही है' },
    instructions: [
      { en: 'Make sure your face is fully illuminated', hi: 'सुनिश्चित करें कि आपका चेहरा पूरी तरह प्रकाशित हो' },
      { en: 'There is no shadow on your face', hi: 'आपके चेहरे पर कोई छाया न हो' },
      { en: 'Background is not too bright', hi: 'बैकग्राउंड में बहुत अधिक रोशनी न हो' },
    ],
    entryVoicePrompt:
      'Now we will check the lighting conditions. Make sure your face is fully illuminated. There is no shadow on your face. And the background is not too bright',
  //   successMessage: 'Excellent!',
  // },
  // {
  //   id: 2,
  //   type: 'GLASS',
  //   name: 'Checking for dark glasses',
  //   instructions: ['Do not wear any dark glasses', 'Remove your glasses'],
  //   entryVoicePrompt:
  //     'Now we will check if you are wearing dark glasses. Make sure you are not wearing one.',
  //   successMessage: 'Excellent!',
  // },
  // {
  //   id: 3,
  //   type: 'MASK',
  //   name: 'Checking for mask',
  //   instructions: [
  //     'Make sure you are not wearing a mask',
  //     'Your entire face should be properly visible',
  //   ],
  //   entryVoicePrompt:
  //     'Now we will check if you are wearing a mask. Make sure your entire face is properly visible.',
    successMessage: {
      en: 'Great! Now we will ask you to perform some actions',
      hi: 'बढ़िया! अब हम आपसे कुछ क्रियाएँ करने को कहेंगे',
    },
  },
  // {
  //   id: 2,
  //   type: 'BLINK',
  //   name: 'Blink your eyes',
  //   instructions: ['Blink your eyes slowly a few times'],
  //   entryVoicePrompt: 'Now, blink your eyes slowly a few times.',
  //   successMessage: 'Excellent!',
  // },
  {
    id: 2,
    type: 'HAND',
    name: { en: 'Raise your hand', hi: 'अपना हाथ उठाएँ' },
    instructions: [
      { en: 'Raise your hand', hi: 'अपना हाथ उठाएँ' },
      { en: 'Show your palm', hi: 'अपनी हथेली दिखाएँ' },
    ],
    entryVoicePrompt: 'Please raise your hand',
    successMessage: { en: 'Excellent!', hi: 'शानदार!' },
  },
]

// Reducer for managing the steps and status
const initialState = {
  currentStep: 0,
  status: 'pending', // 'pending', 'success', or 'fail'
  completed: false,
  warningMessage: '',
  faceCountWarningMessage: '',
  enteredAt: 0, // Initialize to 0
  successAt: null,
  receivedSuccess: false, // Initialize as false
  successCount: 0, // Initialize success count
}

const inputResolution = { height: 500, width: 500, facingMode: 'user', frameRate: { ideal: 15, max: 20 } }

const SHOW_SUCCESS_FOR = 2000 // Show success message for 7 seconds
const MULTI_FACE_LATCH = 2

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'NEXT_STEP':
      // const isLastStep = state.currentStep + 1 >= action.totalSteps // Check if the current step is the last one
      const isLastStep = state.currentStep + 1 === action.totalSteps // Check if the current step is the last one
      return {
        ...state,
        currentStep: state.currentStep + 1,
        status: 'pending',
        completed: isLastStep, // Mark as completed if the last step is reached
        enteredAt: Date.now(), // Set the entry time for the new step
        successAt: null,
        receivedSuccess: false, // Reset the flag to move to the next step
        successCount: 0,
      }
    case 'UPDATE_STATUS':
      return {
        ...state,
        status: action.payload.status,
        warningMessage: action.payload.warningMessage,
      }
    case 'SET_ENTERED_AT':
      return {
        ...state,
        enteredAt: action.payload,
      }
    case 'RECEIVE_SUCCESS':
      const reqSuccessCount = action.payload.requiredSuccessCount || 3
      return {
        ...state,
        successCount: state.successCount + 1, // <-- Increment success count
        receivedSuccess: state.successCount + 1 >= reqSuccessCount, // <-- Set receivedSuccess if count reaches 3
        // receivedSuccess: true, // Set flag to true after 5 seconds to show the message
        // successAt: Date.now(), // Record when success was received
      }
    case 'RESET_SUCCESS_COUNT':
      return {
        ...state,
        successCount: 0, // <-- Reset success count
        receivedSuccess: false,
      }
    case 'SET_SUCCESS_AT':
      return {
        ...state,
        successAt: Date.now(), // Record when success was received
      }
    case 'SET_FACE_COUNT_WARNING':
      return {
        ...state,
        faceCountWarningMessage: action.payload,
      }
    default:
      return state
  }
}

export default function Video({ faceDetection, onContinue, id, socketData, webSocketRef, timeOut }: any) {
  // Component states
  const [isLoading, setIsLoading] = useState(false)
  const [isRecording, setRecording] = useState(false)

  // --- Warning System and Debouncing ---
  const [state, dispatch] = useReducer(reducer, {
    ...(initialState as State),
    enteredAt: Date.now(),
  })

  // References
  const webcamRef = useRef<Webcam>(null)
  const mediaRecorderRef = useRef<MediaRecorder>(null)

  const livenessRef = useRef<{
    lighting?: number
    // glass?: number
    // mask?: boolean
    // blink?: boolean
    hand?: boolean
  }>({})

  const multiFaceFrames = useRef(0)

  const [webcamKey, setWebcamKey] = useState(0)
  const [cameraError, setCameraError] = useState(false)
  const camAttempts = useRef(0)
  const onCameraError = (err: string | DOMException) => {
    console.error('camera failed to start', err)
    if (camAttempts.current < 4) {
      camAttempts.current += 1
      setTimeout(() => setWebcamKey((k) => k + 1), 700)
    } else setCameraError(true)
  }
  const retryCamera = () => {
    camAttempts.current = 0
    setCameraError(false)
    setWebcamKey((k) => k + 1)
  }

  const [updateKyc] = useUpdateKycMutation()
  const [searchParams] = useSearchParams()

  const token = searchParams.get('token')

  // Combine fixed steps with shuffled random steps
  const allSteps = [...fixedSteps]
  // const allSteps = [...fixedSteps, ...shuffleArray(randomSteps)]

  // Get current step data based on currentStep in the reducer
  const currentStepData = allSteps[state.currentStep]

  // --- Helper Functions ---
  const sendDataToSocket = async () => {
    if (state.completed) {
      console.log('All steps are completed. Stopping data transmission.')
      return
    }

    if (!webcamRef.current || !webSocketRef.current) return
    const image = webcamRef.current.getScreenshot()
    if (image && webSocketRef.current.readyState === WebSocket.OPEN) {
      if (mediaRecorderRef.current === null) {
        recordKycProcess()
        return
      }
      console.log('Sending verification for: ', currentStepData.type)
      const data = { id, image, type: currentStepData.type }
      webSocketRef.current.send(JSON.stringify(data))
    }
  }

  const recordKycProcess = async () => {
    if (state.currentStep === 0 && mediaRecorderRef.current === null) {
      await handleStartRecording(webcamRef, mediaRecorderRef, setRecording, id, 'customer', timeOut, false)
    }
  }

  // Check if the captured image is blurry
  // const checkBlurriness = async (image: string | null | undefined) => {
  //   if (image) {
  //     const payload = { input_image: image.split(',')[1] }
  //     const response = await visionAxios.post(`/detect_blurriness`, payload)
  //     return response.data
  //   }
  // }

  // Handle image capture and uploading
  const handleCapture = async () => {
    try {
      // const image = webcamRef.current?.getScreenshot()
      // if (!image) throw new Error('Failed to capture image')
      // setIsLoading(true)
      // await handleStartRecording(webcamRef, mediaRecorderRef, setRecording, id, 'customer')
      // await updateKyc({ image, id, status: 'video_uploaded' })
      // setIsLoading(false)
      await updateKyc({ id, status: 'video_uploaded' })
      onContinue()

      // const checkBlur = await checkBlurriness(image)
      // if (checkBlur.is_blurry === 'True') {
      //   enqueueSnackbar('Your photo is blurry, please try again', {
      //     variant: 'error',
      //     anchorOrigin: { vertical: 'top', horizontal: 'center' },
      //   })
      //   setIsLoading(false)
      // } else {
    } catch (error) {
      console.error(error)
      setIsLoading(false)
    }
  }

  const multiDetectedRef = useRef(false)

  // --- Face Detection and Step Management ---
  useEffect(() => {
    const detectFaces = async () => {
      if (webcamRef.current?.video?.readyState === 4 && faceDetection) {
        try {
          const face = await faceDetection.detectForVideo(
            webcamRef.current.video,
            performance.now()
          )
          const numFaces = face.detections.length
          if (numFaces > 1) {
            multiFaceFrames.current += 1
            if (multiFaceFrames.current >= MULTI_FACE_LATCH) {
              multiDetectedRef.current = true
            }
            dispatch({
              type: 'SET_FACE_COUNT_WARNING',
              payload: t({ en: 'Ensure no one else is in the frame', hi: 'सुनिश्चित करें कि फ़्रेम में कोई और न हो' }),
            })
          } else {
            dispatch({
              type: 'SET_FACE_COUNT_WARNING',
              payload:
                numFaces === 0
                  ? t({ en: 'Ensure your face is fully visible', hi: 'सुनिश्चित करें कि आपका चेहरा पूरी तरह दिखे' })
                  : '',
            })
          }
        } catch (error) {
          console.error('Error in face detection:', error)
        }
      }
    }

    // const animationFrameId = requestAnimationFrame(detectFaces)
    // return () => cancelAnimationFrame(animationFrameId)
    const intervalId = setInterval(detectFaces, 250) // check ~4x/sec to catch brief moments
    return () => clearInterval(intervalId)
  }, [faceDetection])

  useEffect(() => {
    const intervalId = setInterval(sendDataToSocket, 1000)
    return () => clearInterval(intervalId)
  }, [state.currentStep])

  useEffect(() => {
    if (id) updateKyc({ token, id, status: 'video_page' })
    // const intervalId = setInterval(sendDataToSocket, 1000)
    // return () => clearInterval(intervalId)
  }, [id, token])

  const handleSocketData = (socketData: any) => {
    // if (state.completed) return // Prevent further processing after all steps are completed
    if (state.completed) {
      onContinue()
      return
    } // Prevent further processing after all steps are completed
    if (state.receivedSuccess) return
    if (state.faceCountWarningMessage) return // Prevent success message from being spoken multiple times
    if (currentStepData.type === 'LIGHT') {
      // console.log(state)
      if (socketData.lighting_detected === 1) {
        dispatch({
          type: 'UPDATE_STATUS',
          payload: {
            status: 'fail',
            warningMessage: t({ en: 'You are in an underexposed area', hi: 'आप कम रोशनी वाले क्षेत्र में हैं' }),
          },
        })
      } else if (socketData.lighting_detected === 5) {
        dispatch({
          type: 'UPDATE_STATUS',
          payload: {
            status: 'fail',
            warningMessage: t({ en: 'You are in an overexposed area', hi: 'आप अत्यधिक रोशनी वाले क्षेत्र में हैं' }),
          },
        })
      } else if (socketData.lighting_detected === 3) {
        dispatch({ type: 'RECEIVE_SUCCESS', payload: { requiredSuccessCount: 3 } }) // Mark success received
      }
    // } else if (currentStepData.type === 'GLASS') {
    //   // console.log('GLass detection: ', socketData)
    //   if (socketData.glass_detected === -1) {
    //     dispatch({
    //       type: 'UPDATE_STATUS',
    //       payload: { status: 'fail', warningMessage: 'You are too far from the camera' },
    //     })
    //   } else if (socketData.glass_detected === 0) {
    //     dispatch({ type: 'RECEIVE_SUCCESS', payload: { requiredSuccessCount: 3 } })
    //   } else {
    //     dispatch({
    //       type: 'UPDATE_STATUS',
    //       payload: { status: 'fail', warningMessage: 'Ensure you are not wearing glasses' },
    //     })
    //   }
    // } else if (currentStepData.type === 'MASK') {
    //   if (socketData.mask_detected) {
    //     dispatch({
    //       type: 'UPDATE_STATUS',
    //       payload: { status: 'fail', warningMessage: 'Keep your face fully visible, no mask' },
    //     })
    //   } else {
    //     // console.log('No mask detected, sending success')
    //     dispatch({ type: 'RECEIVE_SUCCESS', payload: { requiredSuccessCount: 3 } })
    //   }
    }
    // else if (currentStepData.type === 'BLINK' && socketData.blink_detected) {
    //   dispatch({ type: 'RECEIVE_SUCCESS', payload: { requiredSuccessCount: 1 } })
    // }
    else if (currentStepData.type === 'HAND' && socketData.hands_detected) {
      dispatch({ type: 'RECEIVE_SUCCESS', payload: { requiredSuccessCount: 2 } })
    }
  }

  useEffect(() => {
    if (socketData) {
      const l = livenessRef.current
      if (typeof socketData.lighting_detected === 'number') l.lighting = socketData.lighting_detected
      // if (typeof socketData.glass_detected === 'number') l.glass = socketData.glass_detected
      // if (typeof socketData.mask_detected !== 'undefined') l.mask = !!socketData.mask_detected
      // if (socketData.blink_detected) l.blink = true
      if (socketData.hands_detected) l.hand = true
      handleSocketData(socketData)
    }
    if (state.completed) handleStopRecording(mediaRecorderRef)
  }, [socketData, state.completed])

  const livenessSaved = useRef(false)
  useEffect(() => {
    if (!state.completed || livenessSaved.current) return
    livenessSaved.current = true

    const LIGHT_LABELS: Record<number, string> = {
      1: '1 (Underexposed)',
      2: '2 (Low)',
      3: '3 (Normal)',
      4: '4 (Bright)',
      5: '5 (Overexposed)',
    }
    const l = livenessRef.current
    const liveness_data = {
      light_condition: l.lighting ? LIGHT_LABELS[l.lighting] || `${l.lighting}` : '-',
      // darkglass_on: l.glass === 0 ? 'No' : (l.glass ?? -1) > 0 ? 'Yes' : '-',
      // face_covered: typeof l.mask === 'undefined' ? '-' : l.mask ? 'Yes' : 'No',
      // liveness_factor: `Hand Movement: ${l.hand ? 'Yes' : 'No'}, Blink: ${l.blink ? 'Yes' : 'No'}`,
      liveness_factor: `Hand Movement: ${l.hand ? 'Yes' : 'No'}`,
    }
    updateKyc({ id, status: 'video_uploaded', liveness_data, is_multiple_face_detected: multiDetectedRef.current })
  }, [state.completed])

  useEffect(() => {
    dispatch({ type: 'SET_ENTERED_AT', payload: Date.now() }) // Set time when entering a new step
  }, [state.currentStep])

  useEffect(() => {
    const timer = setInterval(() => {
      const now = Date.now()
      const timeSpent = now - state.enteredAt
      const successTimeSpent = state.successAt ? now - state.successAt : 0
      // console.log('Success count for current state:', state.successCount)
      // console.log('successTimeSpent in loop: ', successTimeSpent)
      // After 5 seconds in the current state, show the success message if success was received
      if (state.receivedSuccess && timeSpent >= 4000 && state.status !== 'success') {
        // console.log('Success received and 4s spent in this state')
        // Success received, showing success message after 4 seconds
        dispatch({ type: 'UPDATE_STATUS', payload: { status: 'success', warningMessage: '' } })
        dispatch({ type: 'SET_SUCCESS_AT' }) // Record when success was received
      }

      // After the success message has been shown for 4 seconds, move to the next step
      if (state.successAt && successTimeSpent >= SHOW_SUCCESS_FOR) {
        // console.log('After success time spent: ', successTimeSpent)
        // console.log('After success over, changing to next step, Sec: ', SHOW_SUCCESS_FOR)
        dispatch({ type: 'NEXT_STEP', totalSteps: allSteps.length })
      }
    }, 500) // Interval of 500ms to check time regularly

    return () => clearInterval(timer) // Cleanup the timer when the effect is re-run or component is unmounted
  }, [state.enteredAt, state.receivedSuccess, state.successAt, state.status])

  if (!faceDetection) return <CircularProgress />
  return (
    <Box
      position="absolute"
      bgcolor={'#232323'}
      // bgcolor="red"
      height="100vh"
      width="100%"
      overflow="hidden"
      top={0}
      left={0}
      right={0}
    >
      <Box position="fixed" zIndex={1} bgcolor={'black'} top="0%" left={0} right={0} height="65%">
        <Webcam
          key={webcamKey}
          screenshotFormat="image/png"
          ref={webcamRef}
          videoConstraints={inputResolution}
          mirrored={true}
          style={{ height: '100%', width: '100%', objectFit: 'cover' }}
          onUserMedia={() => {
            camAttempts.current = 0
            setCameraError(false)
          }}
          onUserMediaError={onCameraError}
          screenshotQuality={0.6}
          imageSmoothing={false}
        />
        <OvalOverlay />
        {cameraError && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              px: 3,
              textAlign: 'center',
              color: '#fff',
            }}
          >
            <Typography variant="subtitle1">
              {t({
                en: 'Couldn’t start the camera. Make sure no other app is using it, then try again.',
                hi: 'कैमरा शुरू नहीं हो सका। सुनिश्चित करें कि कोई अन्य ऐप इसका उपयोग नहीं कर रहा है, फिर पुनः प्रयास करें।',
              })}
            </Typography>
            <Button variant="contained" onClick={retryCamera}>
              {t({ en: 'Retry camera', hi: 'कैमरा पुनः आज़माएँ' })}
            </Button>
          </Box>
        )}
      </Box>
      <KycFaceInstructions
        currentStepData={currentStepData}
        currentStepStatus={state.status}
        handleCapture={handleCapture}
        warningMessage={state.warningMessage}
        faceCountWarningMessage={state.faceCountWarningMessage}
        isCompleted={state.completed}
        isRecording={isRecording}
        isLoading={isLoading}
        showSuccessFor={SHOW_SUCCESS_FOR}
      />
    </Box>
  )
}
